import {
    Box3,
    BufferGeometry,
    Group,
    InstancedMesh,
    Material,
    Matrix4,
    Mesh,
    MeshPhysicalMaterial,
    MeshStandardMaterial,
    Object3D,
    Vector3,
    Color
} from "three";
import {mergeGeometries} from "three/examples/jsm/utils/BufferGeometryUtils";
import {Object3DFactoryService} from "../service/object3D.factory.service";
import {LamellaObject} from "./lamellaObject";
import {MaterialService} from "../service/material/material.service";
import {CustomMaterialType, materialMap} from "../service/material/custom-material-type.enum";
import {dispose3dResource} from "../util/dispose-object";

export class LamellaSetObject extends Group {
    // Oletusmateriaali, jos MaterialService ei vielä aseta omaansa.
    private lamellaSetMaterial: Material = new MeshPhysicalMaterial({
        color: new Color(0x777777),
        roughness: 0.5,
        metalness: 0.5,
    });

    private lamellaSetInstancedMesh: InstancedMesh;

    // LamellaObject, jonka geometry yhdistetään (extractCombinedGeometry)
    private lamellaObject: LamellaObject | undefined;
    private useShadows: boolean = true;

    constructor(
        public data: any,
        private object3DFactoryService: Object3DFactoryService,
        private materialService: MaterialService
    ) {
        super();
        this.name = `LamellaSetObject - ${data.name}`;
        (this.lamellaSetMaterial as MeshPhysicalMaterial).color.set(this.data.color.HEX)
    }

    public async init(): Promise<void> {
        return this.object3DFactoryService.createLamellaObject(this.data)
            .then((lamellaObject) => {
                this.lamellaObject = lamellaObject;

                const { geometry } = this.extractCombinedGeometry(this.lamellaObject);

                if (!geometry) {
                    console.warn("Lamella geometry could not be extracted.");
                    return;
                }

                const maxHeight = 1053; //tämän voisi siirtää yhden tason ylmmäksi

                let count: number = this.data.lamellaRepeat === Infinity
                    ? Math.floor(maxHeight / (this.data.lamellaSpace + this.data.lamellaSpaceBetween))
                    : this.data.lamellaRepeat;

                const lamellaSpaceTaken = (this.data.lamellaSpace + this.data.lamellaSpaceBetween) / 1000;
                this.lamellaSetInstancedMesh = new InstancedMesh(geometry, this.lamellaSetMaterial, count);
                this.lamellaSetInstancedMesh.name = `LamellaSetInstancedMesh - ${this.data.name}`;
                this.lamellaSetInstancedMesh.castShadow = this.useShadows;
                this.lamellaSetInstancedMesh.receiveShadow = this.useShadows

                for (let i = 0; i < count; i++) {
                    const position = new Vector3(0, i * lamellaSpaceTaken, 0);
                    const matrix = new Matrix4().setPosition(position);
                    this.lamellaSetInstancedMesh.setMatrixAt(i, matrix);
                }
                this.lamellaSetInstancedMesh.instanceMatrix.needsUpdate = true;
                this.add(this.lamellaSetInstancedMesh);

                this.materialService.getMaterial(materialMap[this.data.material]).then((material) => {
                    this.lamellaSetInstancedMesh.material = this.lamellaSetMaterial = material;
                    this.setColor(this.data.color.HEX);
                });
            }).catch(console.error)
    }

    /**
     * Asettaa lamellaryhmän värin.
     */
    public setColor(color: Color | string | number): void {
        if (!this.lamellaSetMaterial) {
            console.warn("LamellaSetMaterial is not defined.");
            return;
        }

        if ("color" in this.lamellaSetMaterial) {
            const newColor = new Color(color);
            (this.lamellaSetMaterial as MeshPhysicalMaterial).color.set(newColor);
            this.lamellaSetMaterial.needsUpdate = true;
        } else {
            console.warn("Selected material does not support color property.");
        }
    }

    public setMaterial(materialType: string, color: Color | string | number): Promise<void> {
        const typeEnum = materialType === "Powder Coated" ? CustomMaterialType.PowderCoated : CustomMaterialType.AnodizedAluminum

        return this.materialService
            .getMaterial(typeEnum)
            .then(material => {
                // kun materiaali saatu, päivitä mesh
                this.lamellaSetInstancedMesh.material =
                    this.lamellaSetMaterial = material;
                this.setColor(color);
            });

    }

    /**
     * Skaalaa lamellaryhmän materiaalin pituuden uuteen arvoon.
     */
    public setLength(width: number): void {
        const boundingBox = new Box3().setFromObject(this as Object3D);
        const currentWidth = boundingBox.max.x - boundingBox.min.x;

        if (currentWidth > 0) {
            const scaleFactor = width / currentWidth;
            this.scale.x *= scaleFactor;

            // Estä tekstuurin venyminen (jos käytät esim. MeshStandardMaterial.map)
            if (this.lamellaSetMaterial instanceof MeshStandardMaterial) {
                if (this.lamellaSetMaterial.map) {
                    this.lamellaSetMaterial.map.repeat.x *= scaleFactor;
                    this.lamellaSetMaterial.map.needsUpdate = true;
                }
            }
        }

        if (this.lamellaSetMaterial) {
            this.lamellaSetMaterial.needsUpdate = true;
        }
    }

    /**
     * Etsii ja yhdistää (merge) lamellaObjectin Mesh-geometriat.
     */
    private extractCombinedGeometry( object: Object3D | undefined): { geometry: BufferGeometry | null; material: Material | null } {
        if (!object) {
            return { geometry: null, material: null };
        }

        const geometries: BufferGeometry[] = [];
        let material: Material | null = null;

        object.updateMatrixWorld(true);

        object.traverse((child) => {
            if (child instanceof Mesh && child.geometry) {
                child.updateMatrixWorld(true);
                const cloned = child.geometry.clone();
                cloned.applyMatrix4(child.matrixWorld);
                geometries.push(cloned);
            }
        });

        if (geometries.length === 0) {
            return { geometry: null, material: null };
        }

        const mergedGeometry = mergeGeometries(geometries);
        return { geometry: mergedGeometry, material };
    }

    /**
     * Tuhoaa LamellaSetObjectin ja vapauttaa sen resurssit.
     */
    public destroy(): void {

        this.lamellaSetMaterial = null as any;
        this.lamellaObject = undefined;

        delete this.data;
        delete this.object3DFactoryService;
        delete this.materialService;

        dispose3dResource(this);

    }

}
